import { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Button } from '@/components/ui/button';
import { LogOut, Loader2 } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ImpersonationBanner = () => {
  const auth = getAuth();
  const functions = getFunctions();
  const currentUser = auth.currentUser;
  
  const [isImpersonatingOwner, setIsImpersonatingOwner] = useState(false);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [processingExit, setProcessingExit] = useState(false);
  const { toast } = useToast();

  const portal = localStorage.getItem('portal');

  useEffect(() => {
    const checkImpersonationStatus = async () => {
      if (!currentUser) {
        setIsImpersonatingOwner(false);
        return;
      }
      
      try {
        // We need to decode the token to see claims, or check Redux. 
        // Checking token directly is safer for "global" banner state.
        const idTokenResult = await currentUser.getIdTokenResult();
        const claims = idTokenResult.claims;
        
        // 1. Check for explicit 'isImpersonating' marker (newly deployed logic)
        // 2. OR check if 'owner' role is in affiliations but current portal != owner
        // This covers the case where the user IS an owner, but is viewing another portal.
        
        const affiliations = (claims.affiliations as any[]) || [];
        const hasOwnerRole = affiliations.some((aff: any) => aff.role === 'owner');
        const isImpersonatingClaim = !!claims.isImpersonating;

        // If I am an owner (in reality), and I am NOT in owner portal view -> Show banner
        // OR if the explicit flag is set.
        if (hasOwnerRole && portal !== 'owner') {
             setIsImpersonatingOwner(true);
        } else if (isImpersonatingClaim) {
             setIsImpersonatingOwner(true);
        } else {
             setIsImpersonatingOwner(false);
        }

      } catch (error) {
        console.error("Error checking token claims:", error);
      }
    };

    checkImpersonationStatus();
  }, [currentUser, portal]); // Re-check if user or portal changes

  if (!isImpersonatingOwner) return null;

  const executeExitImpersonation = async (e: React.MouseEvent) => {
    e.preventDefault();
    setProcessingExit(true);

    try {
      const stopImpersonation = httpsCallable(functions, 'stopImpersonation');
      await stopImpersonation();
      
      // Force token refresh to clear claims
      await currentUser?.getIdToken(true);
      
      localStorage.setItem('portal', 'owner');
      
      toast({
        title: "Exiting School View",
        description: "Restoring owner dashboard access...",
        duration: 3000,
      });

      // Redirect immediately to owner dashboard
      setTimeout(() => {
        window.location.href = '/owner-dashboard';
      }, 1500);
      
    } catch (error) {
      console.error("Failed to stop impersonation:", error);
      toast({
        title: "Error",
        description: "Failed to exit school view. Retrying...",
        variant: "destructive",
      });
      // Fail-safe: even if Cloud Function fails, try to redirect owner
      // But clearing claims is important. If cloud function failed, we might be stuck.
      // We'll offer a 'Force Quit' if it fails repeatedly? 
      // For now, let's assume transient error and allow retry.
      setProcessingExit(false);
      setExitConfirmOpen(false);
    }
  };



  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-100 border-b border-amber-200 px-6 py-2 flex items-center justify-between shadow-md">
        <span className="text-amber-900 font-medium text-sm flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-amber-500 animate-pulse"></div>
          You are viewing this school as an {portal === 'school_staff' ? 'Staff Member' : portal === 'student' ? 'Student' : portal === 'guardian' ? 'Guardian' : 'Administrator'}
        </span>
        <Button 
          size="sm" 
          variant="outline" 
          onClick={() => setExitConfirmOpen(true)}
          className="bg-white hover:bg-amber-50 text-amber-900 border-amber-200 h-8 text-xs font-semibold gap-2"
        >
          <LogOut className="h-3 w-3" />
          Exit School View
        </Button>
      </div>

      {/* Spacer to push content down so banner doesn't overlap header */}
      <div className="h-12" />

      <AlertDialog open={exitConfirmOpen} onOpenChange={setExitConfirmOpen}>
        <AlertDialogContent className="border border-slate-200 dark:border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle>Exit School View?</AlertDialogTitle>
            <AlertDialogDescription>
              This will restore your Owner privileges and modify your portal view back to the Owner Dashboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processingExit}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={executeExitImpersonation} 
              className="bg-amber-600 hover:bg-amber-700 ml-2"
              disabled={processingExit}
            >
              {processingExit ? (
                <>
                   <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                   Processing...
                </>
              ) : (
                'Proceed'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ImpersonationBanner;
